// TODO(orcs-migration): Temporary layout: local `Preview` + `AvatarModalPreviewTrigger` instead of ORCS
// `Avatar` `headerAvatar` + `customSize` — frozen ORCS cannot size header avatar to match EditMode; revert to
// design-system component once migration allows ORCS changes (see Avatar `headerAvatar` / custom outer size).

import React, { useState, useCallback, useRef } from "react";
import { Flex, Avatar, Button, P } from "orcs-design-system";
import PropTypes from "prop-types";
import Preview from "./Preview";
import { readFile } from "./utils";
import { AVATAR_MODAL_CROP_PX } from "./const";
import { AvatarModalPreviewTrigger } from "./styled.components";

const cropDisplayWidth = `${AVATAR_MODAL_CROP_PX}px`;

const PreviewMode = ({
  onImageChange,
  imageSrc,
  croppedArea,
  shape,
  acceptedImageTypes,
  onEdit,
  onDeleteAvatar,
  isDeleting,
}) => {
  const [validationError, setValidationError] = useState([]);

  const fileInputRef = useRef(null);

  const onFileChange = useCallback(
    async (files) => {
      setValidationError([]);
      if (files && files.length > 0) {
        const file = files[0];
        if (!acceptedImageTypes.includes(file.type)) {
          setValidationError([
            `Invalid image type!`,
            `Accepted types: ${acceptedImageTypes
              .map((type) => type.replace("image/", ""))
              .join(", ")}`,
          ]);
          return;
        }
        const imageDataUrl = await readFile(file);

        onImageChange(imageDataUrl);
      }
    },
    [acceptedImageTypes, onImageChange]
  );

  const onImageDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];

        if (!acceptedImageTypes.includes(file.type)) {
          setValidationError([
            `Invalid image type!`,
            `Accepted types: ${acceptedImageTypes
              .map((type) => type.replace("image/", ""))
              .join(", ")}`,
          ]);
          return;
        }

        onFileChange(e.dataTransfer.files);
        e.dataTransfer.clearData();
      }
    },
    [acceptedImageTypes, onFileChange]
  );

  const onImageInputChange = useCallback(
    (e) => {
      if (e.target.files && e.target.files.length > 0) {
        onFileChange(e.target.files);
      }
    },
    [onFileChange]
  );

  const onUploadNewImage = useCallback(() => {
    fileInputRef.current.click();
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onImageKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onUploadNewImage();
      }
    },
    [onUploadNewImage]
  );

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      width="400px"
      gap="l"
      mt="12px"
    >
      <Flex
        onKeyDown={onImageKeyDown}
        onDrop={onImageDrop}
        onDragOver={onDragOver}
        width="400px"
        flexDirection="column"
        alignItems="center"
        gap="r"
      >
        <input
          type="file"
          accept={acceptedImageTypes.join(",")}
          ref={fileInputRef}
          onChange={onImageInputChange}
          style={{ display: "none" }}
        />

        {imageSrc ? (
          <AvatarModalPreviewTrigger
            type="button"
            onClick={onUploadNewImage}
            aria-label="Upload profile picture"
            $width={cropDisplayWidth}
          >
            <Preview
              borderBoxWidth
              croppedArea={croppedArea}
              imageSrc={imageSrc}
              cropShape={shape}
              width={cropDisplayWidth}
              borderSize="6px"
            />
          </AvatarModalPreviewTrigger>
        ) : (
          <AvatarModalPreviewTrigger
            type="button"
            onClick={onUploadNewImage}
            aria-label="Upload profile picture"
            $width={cropDisplayWidth}
          >
            <Avatar shape={shape} customSize={cropDisplayWidth} />
          </AvatarModalPreviewTrigger>
        )}

        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          gap="s"
        >
          {validationError?.length > 0 ? (
            validationError.map((error) => (
              <P variant="danger" key={error}>
                {error}
              </P>
            ))
          ) : (
            <P textAlign="center">
              Drag and drop an image here, or click to upload
            </P>
          )}
        </Flex>
      </Flex>

      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-end"
        gap="10px"
        width="100%"
      >
        {imageSrc && (
          <Button
            ariaLabel="Edit/Crop image"
            onClick={onEdit}
            disabled={isDeleting}
          >
            Edit
          </Button>
        )}

        <Button
          ariaLabel="Upload"
          onClick={onUploadNewImage}
          disabled={isDeleting}
        >
          Upload
        </Button>

        {imageSrc && (
          <Button
            ariaLabel="Delete image"
            variant="danger"
            onClick={onDeleteAvatar}
            isLoading={isDeleting}
            disabled={isDeleting}
          >
            Delete
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

PreviewMode.propTypes = {
  onImageChange: PropTypes.func.isRequired,
  imageSrc: PropTypes.string.isRequired,
  croppedArea: PropTypes.object,
  shape: PropTypes.string.isRequired,
  acceptedImageTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDeleteAvatar: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool.isRequired,
};

export default PreviewMode;
